'use client';

import { useGsapContext } from 'hooks/animation/useGsapContext';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useRef } from 'react';
import {
  BookCallTFComponentDiv,
  CustomImage as Image,
  useModal,
} from '../shared';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { FlatButton } from 'components/shared';
import styles from './CircularShowcase.module.css';

export const CircularShowcase = ({ className }: { className?: string }) => {
  const container = useRef<HTMLDivElement>(null);
    const ImageTwo = useRef<HTMLDivElement>(null);
    const ImageThree = useRef<HTMLDivElement>(null);
    const ImageFour = useRef<HTMLDivElement>(null);
    const ImageFive = useRef<HTMLDivElement>(null);
    const ImageSix = useRef<HTMLDivElement>(null);
    const textContainer = useRef<HTMLDivElement>(null);
    const gsapContext = useGsapContext();

    const ModalClient = useModal();

    const circularAnimation = () => {
      if (!container.current) return;

      gsapContext.add(() => {
        const tl = gsap.timeline({
          defaults: {
            duration: 1.5,
            ease: 'none',
          },
          scrollTrigger: {
            trigger: container.current,
            start: 'top 50%',
            end: 'bottom bottom',
            scrub: 1,
            immediateRender: false,
          },
        });

        tl.set(
          [
            ImageTwo.current,
            ImageThree.current,
            ImageFour.current,
            ImageFive.current,
            ImageSix.current,
          ],
          {
            transformOrigin: 'center bottom',
          },
        );
        tl.set(textContainer.current, {
          autoAlpha: 0,
        });

        tl.fromTo(
          [
            ImageTwo.current,
            ImageThree.current,
            ImageFour.current,
            ImageFive.current,
            ImageSix.current,
          ],
          {
            rotation: 0,
            xPercent: 0,
            yPercent: 0,
          },
          {
            rotation: -50,
            xPercent: -20,
            yPercent: 5,
            duration: 0.5,
          },
        );
        // return;
        tl.fromTo(
          [
            ImageThree.current,
            ImageFour.current,
            ImageFive.current,
            ImageSix.current,
          ],
          {
            rotation: -50,
            xPercent: -20,
            yPercent: 5,
          },
          {
            rotation: -130,
            xPercent: -20,
            yPercent: -5,
            duration: 0.5,
          },
        );

        // return;
        tl.fromTo(
          [ImageFour.current, ImageFive.current, ImageSix.current],
          {
            rotation: -130,
            xPercent: -20,
            yPercent: -5,
          },
          {
            rotation: -180,
            xPercent: 0,
            yPercent: 0,
            duration: 0.5,
          },
        );

        tl.to(textContainer.current, {
          autoAlpha: 1,
          duration: 0.3,
        });

        tl.fromTo(
          [ImageFive.current, ImageSix.current],
          {
            rotation: -180,
            xPercent: 0,
            yPercent: 0,
          },
          {
            rotation: -230,
            xPercent: 20,
            yPercent: -5,
            duration: 0.5,
          },
        );

        tl.fromTo(
          ImageSix.current,
          {
            rotation: -230,
            xPercent: 20,
            yPercent: -5,
          },
          {
            rotation: -310,
            xPercent: 20,
            yPercent: 5,
            duration: 0.5,
          },
        );

        tl.addPause('+=0.5');

        // --
      });
    };

    useIsomorphicLayoutEffect(() => {
      circularAnimation();

      // ResizeObserver to monitor container size changes only
      let lastHeight = 0;
      const debouncedRefresh = () => {
        setTimeout(() => ScrollTrigger.refresh(), 100);
      };

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const currentHeight = entry.contentRect.height;
          // Only refresh if height changed significantly (>10px threshold)
          if (Math.abs(currentHeight - lastHeight) > 10) {
            lastHeight = currentHeight;
            debouncedRefresh();
          }
        }
      });

      // Only observe the specific container instead of entire document
      if (container.current) {
        resizeObserver.observe(container.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    const showBookCallModal = () => {
      let url = 'website';
      if (window) {
        url = window.location.hostname + window.location.pathname;
      }
      ModalClient.set(
        <BookCallTFComponentDiv
          trackingParams={{
            utm_source: url,
            utm_medium: 'product-wizard-section',
          }}
        />,
      );
      ModalClient.setCloseButtonTheme('light');
      ModalClient.show();
    };

    return (
      <section
        className={`${styles.root}${className ? ` ${className}` : ''}`}
        ref={container}
      >
        <div className={styles.sticky}>
          <div className={styles.circularContainer}>
            <div className={styles.imgContainer}>
              <div className={styles.upperBlock}>
                {' '}
                <Image
                  src={'web_v2/hero/got-questions/Footer_1.png'}
                  fill
                  alt="Footer_1"
                />
              </div>
              <div className={styles.lowerBlock}></div>
              <div className={styles.content} ref={textContainer}>
                <p className={styles.text}>Got any questions?</p>
                <FlatButton onClick={showBookCallModal}>
                  Talk to a product specialist
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M12.668 8L3.33464 8M12.668 8L8.66797 12M12.668 8L8.66797 4"
                      stroke="black"
                      strokeWidth="1.33333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </FlatButton>
              </div>
            </div>
            <div className={styles.imgContainer} ref={ImageTwo}>
              <div className={styles.upperBlock}>
                <Image
                  src={'web_v2/hero/got-questions/aster-black-paint.png'}
                  fill
                  alt="uh_2"
                />
              </div>
              <div className={styles.lowerBlock}></div>
            </div>
            <div className={styles.imgContainer} ref={ImageThree}>
              <div className={styles.upperBlock}>
                <Image
                  src={'web_v2/hero/got-questions/uh_4.png'}
                  fill
                  alt="uh_4"
                />
              </div>
              <div className={styles.lowerBlock}></div>
            </div>
            <div className={styles.imgContainer} ref={ImageFour}>
              <div className={styles.upperBlock}>
                <Image
                  src={'web_v2/hero/got-questions/campaign_5.png'}
                  fill
                  alt="campaign_5"
                />
              </div>
              <div className={styles.lowerBlock}></div>
            </div>
            <div className={styles.imgContainer} ref={ImageFive}>
              <div className={styles.upperBlock}>
                <Image
                  src={'web_v2/hero/got-questions/raw_titanium_6.png'}
                  fill
                  alt="raw_titanium_6"
                />
              </div>
              <div className={styles.lowerBlock}></div>
            </div>
            <div className={styles.imgContainer} ref={ImageSix}>
              <div className={styles.upperBlock}>
                <Image
                  src={'web_v2/hero/got-questions/raw_titanium_3.png'}
                  fill
                  alt="raw_titanium_3"
                />
              </div>
              <div className={styles.lowerBlock}></div>
            </div>
          </div>
        </div>
      </section>
    );
};
